import "./tailwind.css";
import "./global.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/grid";
import "@qws-org/ui-kit/styles.css";

import { Title, UIKitProvider } from "@qws-org/ui-kit";
import { withSentry } from "@sentry/remix";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type {
  LoaderFunction,
  MetaFunction,
  MiddlewareFunction,
} from "react-router";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
  useRouteLoaderData,
} from "react-router";
import { cleanDoubleSlashes, parseURL } from "ufo";
import Cookies from "universal-cookie";

import { ClientContext } from "~/contexts/client-context/provider";
import { PluginContextProvider } from "~/contexts/plugin-context";
import { getGtm } from "~/core";
import { getDocuments } from "~/core/deprecated/documents";
import { getPageLayoutService } from "~/core/services/get-page-layout.service";
import { getLootBoxSettings } from "~/core/services/loot-box-settings.service";
import { getNotifications } from "~/core/services/notifications.service";
import { getPageDesignSystem } from "~/core/services/page-design-system.service";
import { getPageLayoutV2 } from "~/core/services/page-layout.service";
import { getLayoutTranslations } from "~/core/services/static-translations.service";
import { SiteSdk } from "~/core/site-sdk";
import {
  domainsSettings,
  PageInfoProvider,
} from "~/features-deprecated/banner/usePageInfoContext";
import {
  affilateMiddleware,
  languageRedirectMiddleware,
  pageRedirectMiddleware,
} from "~/middlewares";
import { rootClientLoader } from "~/root.client-loader";
import {
  affiliateContext,
  authDataContext,
  authFeatureContext,
  availableLanguagesContext,
  cfProxyDataContext,
  fallbackLanguageContext,
  featureFlagsContext,
  languageContext,
  redirectsContext,
  sdkContext,
  settingsContext,
} from "~/server/get-load-context";
import { createOverrideThemeValues } from "~/shared/utils/create-override-theme-values";
import { createTheme } from "~/shared/utils/create-theme";
import { createLinkComponent } from "~/shared/utils/createLinkComponent";
import { DirectusProvider } from "~/shared/utils/directus/directus.provider";
import { FlagsClient } from "~/shared/utils/feature-flags/core";
import { GtmProvider } from "~/shared/utils/gtm-sdk/provider";
import { createGtmInstance } from "~/shared/utils/gtm-sdk/provider/createGtmInstance";
import { AnjouanLicense } from "~/widgets/anjouan-license";
import { AppLayoutWidget } from "~/widgets/app-layout";
import { AuthModalsProvider } from "~/widgets/auth-modals";
import { MainLayout } from "~/widgets/main-layout";
import { NotificationConstructor } from "~/widgets/notifications-constructor";

// eslint-disable-next-line react-refresh/only-export-components
export const middleware: Array<MiddlewareFunction> = [
  languageRedirectMiddleware,
  affilateMiddleware,
  pageRedirectMiddleware,
];

export const loader = (async ({ request, context }) => {
  const url = new URL(request.url);
  const featureFlags = context.get(featureFlagsContext);
  const settings = context.get(settingsContext);
  const sdk = context.get(sdkContext);
  const affiliate = context.get(affiliateContext);
  const { playerID, loginToken, authorizationToken } =
    context.get(authDataContext);
  const authFeature = context.get(authFeatureContext);
  const fallbackLanguage = context.get(fallbackLanguageContext);
  const cfProxyData = context.get(cfProxyDataContext);
  const availableLanguages = context.get(availableLanguagesContext);
  const redirectForClient = context.get(redirectsContext);
  const domainSettings = (Object.entries(domainsSettings).find(([key]) =>
    url.host.startsWith(key),
  ) ?? [])[1];

  const [
    gtm,
    translations,
    pageLayout,
    lootBoxSettings,
    pageLayoutV2,
    pageDesignSystem,
    notifications,
  ] = await Promise.all([
    sdk.request(getGtm()),
    sdk.request(getLayoutTranslations()),
    sdk.request(getPageLayoutService()),
    sdk.request(getLootBoxSettings()),
    sdk.request(getPageLayoutV2(url)),
    sdk.request(getPageDesignSystem(url)),
    sdk.request(getNotifications()),
  ]);

  const { host, protocol } = parseURL(request.url);
  const language = context.get(languageContext);
  return {
    domainSettings,
    documents: getDocuments(),
    pageUrl: url,
    featureFlags: {
      isNavigationEnabled: featureFlags.getItem("headerNavigationLinks")
        .enabled,
      isLanguageSelectorEnabled: featureFlags.getItem("headerLanguageSelector")
        .enabled,
      isCreateButtonEnabled: featureFlags.getItem("headerCreateButton").enabled,
      isFooterNavigationLinkEnabled: featureFlags.getItem(
        "footerNavigationLinks",
      ).enabled,
      isMenuConstructorEnabled: featureFlags.getItem("menuConstructor").enabled,
      isFooterMenuConstructorEnabled: featureFlags.getItem(
        "footerMenuConstructor",
      ).enabled,
      isRegistrationFeatureEnabled: featureFlags.getItem("registration-feature")
        .enabled,
      isLayoutV2Enabled: featureFlags.getItem("layouts_v2").enabled,
      isPreDepBonusEnabled: featureFlags.getItem("bonusBeforeFirstDep").enabled,
      isGtmProxyEnabled: featureFlags.getItem("gtm-proxy").enabled,
    },
    affiliate,
    gtmId: gtm?.container_identifier,
    metricsService: gtm?.metrics_service,
    translations,
    header: { ...pageLayout.header },
    cmsPath: `${protocol}//${host}`,
    sentryDsn: process.env.SENTRY_DSN,
    fontSize: settings.app.font_size,
    fontFamily: settings.app.font_family,
    fontWeight: settings.app.font_weight,
    lineHeight: settings.app.line_height,
    colors: settings.app.colors,
    borderRadius: settings.app.border_radius,
    authFeatures: authFeature,
    availableLanguages,
    flags: featureFlags.getSerializedFlags(),
    authorizationToken,
    loginToken,
    playerID,
    footer: pageLayout.footer,
    supportedLngs: settings.languages.map((l) => l.code),
    fallbackLang: fallbackLanguage,
    cfProxyData,
    favicon: settings.app.favicon,
    languages: settings.languages,
    homePageSlug: settings.app.home?.slug,
    lootBoxSettings,
    hostname: new URL(request.url).hostname,
    layout: settings.app.layout?.[0]?.item,
    pageLayoutV2,
    pageDesignSystem,
    pluginIntegrations: {
      casinoFastRegistration: settings?.casino_fast_registration,
      valueChainRegistration: settings?.value_chain_registration,
      preDepBonus: settings?.first_dep_bonus_plugin,
      valueChainLogin: settings?.value_chain_login,
      otpRegistration: settings?.otp_registration,
      anjouanLicense: settings?.anjouan_licensing,
    },
    notifications,
    redirectForClient,
    language,
  };
}) satisfies LoaderFunction;
export const clientLoader = rootClientLoader;

export function Layout({ children }: { children: ReactNode }): ReactNode {
  const data = useRouteLoaderData<typeof loader>("root");

  const {
    i18n: { language },
  } = useTranslation();
  const gtmId = data?.gtmId;
  const meticsService = data?.metricsService;
  const isGtmProxy = data?.featureFlags?.isGtmProxyEnabled ?? false;

  const siteSdk = new SiteSdk();
  const isLayoutV2Enabled = data?.featureFlags?.isLayoutV2Enabled ?? false;
  const layoutData =
    Object.keys(data?.pageLayoutV2 ?? {}).length > 1
      ? data?.pageLayoutV2
      : data?.layout;

  const pageDesignSystem = data?.pageDesignSystem;
  const anjouanLicensingData = data?.pluginIntegrations?.anjouanLicense ?? null;
  const overrideThemeInput = createOverrideThemeValues({
    currentThemeEntry: {
      colors: data?.colors ?? {},
      fontWeight: data?.fontWeight ?? {},
      fontSize: data?.fontSize ?? {},
      fontFamily: data?.fontFamily ?? {},
      lineHeight: data?.lineHeight ?? {},
      borderRadius: data?.borderRadius ?? {},
    },
    newThemeEntry: {
      colors: pageDesignSystem?.colors ?? {},
      fontWeight: pageDesignSystem?.font_weight ?? {},
      fontSize: pageDesignSystem?.font_size ?? {},
      lineHeight: pageDesignSystem?.line_height ?? {},
      borderRadius: pageDesignSystem?.border_radius ?? {},
    },
  });

  const theme = createTheme(language, overrideThemeInput);

  return (
    <PluginContextProvider>
      <html lang={language}>
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="new-version" content="1.7.1" />
          <Meta />

          {data?.fontFamily?.main_name && data?.fontFamily?.main_url && (
            <link rel="stylesheet" href={data?.fontFamily?.main_url} />
          )}
          {data?.fontFamily?.additional_name &&
            data?.fontFamily?.additional_url && (
              <link rel="stylesheet" href={data?.fontFamily?.additional_url} />
            )}
          {data?.fontFamily?.accent_name && data?.fontFamily?.accent_url && (
            <link rel="stylesheet" href={data?.fontFamily?.accent_url} />
          )}
          {data?.favicon?.id && (
            <link rel="icon" href={`/cms-assets/${data.favicon.id}/`} />
          )}
          <Links />

          {gtmId && !isGtmProxy && (
            <script
              suppressHydrationWarning={true}
              dangerouslySetInnerHTML={{
                __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`,
              }}
            />
          )}
          {gtmId && isGtmProxy && (
            <script
              src={cleanDoubleSlashes(`${meticsService}/metrics/loader.js`)}
              data-gtm-id={gtmId.replace("GTM-", "")}
            />
          )}
          {data?.supportedLngs && data?.fallbackLang && (
            <script
              dangerouslySetInnerHTML={{
                __html: `window.__i18n = ${JSON.stringify({
                  supportedLngs: data.supportedLngs,
                  fallbackLng: data.fallbackLang,
                })}`,
              }}
            />
          )}
          {/*  <!-- Anjouan script  --> */}
          {anjouanLicensingData?.script_url && (
            <script
              type="text/javascript"
              src={anjouanLicensingData?.script_url}
            ></script>
          )}
        </head>
        <body>
          {/* <!-- Google Tag Manager (noscript) --> */}
          {gtmId && (
            <noscript>
              <iframe
                src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
                height="0"
                width="0"
                style={{ display: "none", visibility: "hidden" }}
              />
            </noscript>
          )}

          {/* <!-- End Google Tag Manager (noscript) --> */}
          <ClientContext
            value={
              data
                ? {
                    siteSdk,
                    authToken: data.authorizationToken,
                    loginToken: data.loginToken,
                    affiliate: data.affiliate,
                    featureFlags: new FlagsClient({
                      initialValues: data.flags,
                    }),
                    cookies: new Cookies(),
                    breadcrumbsStaticTranslations:
                      data.translations.static_translations?.breadcrumbs?.[0] ??
                      null,
                    playerID: data.playerID,
                    authFeatures: data.authFeatures,
                    cfProxyData: data.cfProxyData,
                    language,
                    availableLanguages: data.availableLanguages,
                    languages: data.languages,
                    isNewUser: false,
                    anjouanLicensingData,
                  }
                : null
            }
          >
            <UIKitProvider
              value={{
                components: {
                  Link: createLinkComponent(language),
                },
                theme,
              }}
            >
              <GtmProvider sdk={createGtmInstance()}>
                <DirectusProvider url={data?.cmsPath ?? ""}>
                  <AuthModalsProvider
                    siteSdk={siteSdk}
                    playerId={data?.playerID}
                  >
                    <AnjouanLicense />
                    {isLayoutV2Enabled && layoutData ? (
                      <AppLayoutWidget layoutData={layoutData}>
                        <NotificationConstructor
                          data={data?.notifications ?? {}}
                        >
                          {children}
                        </NotificationConstructor>
                      </AppLayoutWidget>
                    ) : (
                      <MainLayout>{children}</MainLayout>
                    )}
                  </AuthModalsProvider>
                </DirectusProvider>
              </GtmProvider>
            </UIKitProvider>
          </ClientContext>
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    </PluginContextProvider>
  );
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data?.hostname === "xingyunpoker.com") {
    return [{ title: "LuckyPoker 休闲扑克玩家的天堂" }];
  }

  return [];
};

function App(): ReactNode {
  const loaderData = useLoaderData<typeof loader>();
  const { pageUrl, domainSettings, sentryDsn } = loaderData;
  const navigationState = useNavigation();
  return (
    <>
      {["loading", "submiting"].includes(navigationState.state) && (
        <div
          className="fixed fixedtop-0 left-0 w-full h-1 bg-blue-500 "
          style={{ zIndex: 99 }}
        />
      )}
      <span
        id={"sentryDsn"}
        data-value={sentryDsn}
        style={{ position: "absolute" }}
      />
      <PageInfoProvider url={pageUrl} domainSettings={domainSettings}>
        <Outlet />
      </PageInfoProvider>
    </>
  );
}
// eslint-disable-next-line
export function ErrorBoundary({ error }: any): ReactNode {
  if (isRouteErrorResponse(error)) {
    return (
      <>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </>
    );
  }

  return (
    <Title>
      Something went wrong, try clearing your browser cache and reloading the
      page.
    </Title>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default withSentry(App);
